import React from 'react';
import { useTranslation } from 'react-i18next';
import { PawPrint, Heart, Activity } from 'lucide-react';

interface AuthHeroProps {
  eyebrow: string;
  headline: string;
  description: string;
  image?: string;
  imageAlt?: string;
  background?: string;
  showAnimations?: boolean;
}

const AuthHero: React.FC<AuthHeroProps> = ({
  eyebrow,
  headline,
  description,
  image,
  imageAlt = 'VetPintar Hero',
  background = 'linear-gradient(135deg, #F8FAFC 0%, #E0E7FF 50%, #F8FAFC 100%)',
  showAnimations = true
}) => {
  const { t } = useTranslation();

  return (
    <div className="relative hidden overflow-hidden px-12 py-14 md:flex md:flex-col" style={{ background }}>
      {showAnimations && (
        <>
          {/* Decorative blur orbs - matching landing pattern */}
          <div className="pointer-events-none absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-violet-600/10 blur-3xl animate-pulse" />
          <div className="pointer-events-none absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-cyan-500/10 blur-3xl animate-pulse delay-1000" />
          <div className="pointer-events-none absolute right-1/4 top-1/3 h-40 w-40 rounded-full bg-violet-400/10 blur-3xl animate-pulse delay-500" />

          {/* Floating icons */}
          <div className="pointer-events-none absolute left-10 top-20 animate-bounce text-violet-600/30">
            <PawPrint className="h-12 w-12" />
          </div>
          <div className="pointer-events-none absolute right-20 top-40 animate-bounce text-cyan-500/30 delay-300">
            <Heart className="h-10 w-10" />
          </div>
          <div className="pointer-events-none absolute bottom-32 left-20 animate-bounce text-violet-400/30 delay-500">
            <Activity className="h-14 w-14" />
          </div>
        </>
      )}

      <div className="relative flex flex-1 flex-col justify-between">
        <div>
          <span className="inline-block rounded-full border border-violet-600/30 bg-violet-100/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-violet-600 shadow-lg backdrop-blur">
            {eyebrow}
          </span>
          <h1 className="mt-6 text-5xl font-bold leading-tight text-gray-900">
            {headline}
          </h1>
          <p className="mt-4 max-w-md text-lg font-medium leading-relaxed text-gray-600">
            {description}
          </p>
        </div>

        {image && (
          <div className="mt-8 flex flex-1 items-center justify-center">
            <img
              src={image}
              alt={imageAlt}
              className="max-h-[420px] w-full max-w-md object-contain"
            />
          </div>
        )}

        <div className="flex flex-col gap-2 text-xs text-gray-600">
          <p>
            &copy; {new Date().getFullYear()} {t('common.appName')}. {t('layout.footer')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthHero;
